import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import EnterpriseMembersProvider from 'providers/EnterpriseMembers';
import { Enterprise, EnterpriseBusinessUnit } from 'models/Enterprises';
import TeamFilters from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/pages/Team/components/TeamFilters';
import TeamMembersTable from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/pages/Team/components/TeamMembersTable';
import { useLockScrollOnParamsReroute } from 'hooks/useLockScrollOnParamsReroute';
import { PAGE_PARAM_KEYWORD } from 'data/global';
import useQueryParams from 'hooks/useQueryParams';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';
import {
  HEADING_TRANSLATIONS,
  REQUIRED_COLUMNS,
  RWC_TABLE_COLUMNS,
  TABLE_COLUMNS,
  LOCKED_COLUMN,
} from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/pages/Team/components/TeamMembersTable/components/TeamMembersTableCells/data';

import './EnterpiseTeam.styles.scss';

type EnterpiseTeamProps = {
  className?: string;
  enterprise: Partial<Enterprise>;
  refetchFlag?: boolean;
  withNavigation?: boolean;
  isRWC?: boolean;
  updateBusinessUnits: (
    updatedBusinessUnits: EnterpriseBusinessUnit[],
    shouldRefetch?: boolean,
  ) => void;
};

const EnterpiseTeam: React.FC<EnterpiseTeamProps> = (props) => {
  const {
    className,
    enterprise,
    refetchFlag,
    withNavigation = true,
    isRWC = false,
    updateBusinessUnits,
  } = props;

  const ref = useRef<HTMLDivElement>();

  const { params } = useQueryParams();

  const page = params?.[PAGE_PARAM_KEYWORD];

  const classes = classNames('enterpise-team', className);

  const defaultColumns = useMemo(
    () => (isRWC ? RWC_TABLE_COLUMNS : TABLE_COLUMNS),
    [isRWC],
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TEAM,
    defaultColumns,
  );

  const manageColumns = useMemo(
    () => allColumns.filter((column) => !REQUIRED_COLUMNS.includes(column)),
    [allColumns],
  );

  const scrollToStartOfTable = useCallback(
    () => ref?.current?.scrollIntoView({ block: 'start' }),
    [],
  );

  useLockScrollOnParamsReroute();

  useEffectSkipFirst(() => {
    scrollToStartOfTable();
  }, [page, scrollToStartOfTable]);

  return (
    <div className={classes} ref={ref}>
      <EnterpriseMembersProvider
        enterprise={enterprise}
        refetchFlag={refetchFlag}
        updateBusinessUnits={updateBusinessUnits}
      >
        <TeamFilters
          withNavigation={withNavigation}
          manageColumns={
            <ManageColumns
              allColumns={manageColumns}
              hiddenColumns={hiddenColumns}
              headingTranslations={HEADING_TRANSLATIONS}
              lockedColumn={LOCKED_COLUMN}
              isDefaultColumns={isDefaultColumns}
              onSave={saveColumnSettings}
              onResetToDefault={resetToDefault}
            />
          }
        />
        <TeamMembersTable
          isRWC={isRWC}
          columns={columns}
          columnSizing={columnSizing}
          onReorder={onReorder}
          onColumnSizingChange={onColumnSizingChange}
        />
      </EnterpriseMembersProvider>
    </div>
  );
};

export default EnterpiseTeam;
